import { Link } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '@/lib/useMe';
import { logoUrl, useBranding, useMarketingPanes } from '@/lib/usePages';
import { VdbBadge } from '@/components/VdbBadge';
import { CapabilityPanes } from '@/components/CapabilityPanes';
import { buttonVariants } from '@/components/ui/Button';

interface HealthResponse {
  version: string;
}

function useVersion() {
  return useQuery<string>({
    queryKey: ['health', 'version'],
    staleTime: 60_000,
    queryFn: async () => {
      const r = await fetch('/api/health');
      const body = (await r.json()) as HealthResponse;
      return body.version;
    },
  });
}

export function WelcomePage() {
  const auth = useAuth();
  const me = useMe();
  const version = useVersion();

  const greeting = friendlyName(auth.user?.profile.email, auth.user?.profile.sub);
  // Per-instance branding: the customer's logo and title in place of the
  // product's, when an Admin has set them. The header keeps the TReS logo
  // and wordmark either way.
  const branding = useBranding();
  const customLogo = logoUrl(branding.data);
  const title = branding.data?.welcome_title ?? 'Welcome to TReS';
  const tagline = branding.data?.welcome_tagline ?? 'LLM Grounding Platform';
  const showTagline = branding.data?.show_tagline ?? true;

  // The marketing panes are per-instance (a customer's site never pitches a
  // site) and, on the instance that shows them, per-audience: `signed-in`
  // keeps them off the public face of a preview while the copy is reviewed,
  // `everyone` reveals them. Both are the operator's env setting, so the
  // reveal is a configuration change rather than a release.
  const marketingPanes = useMarketingPanes();
  const showPanes =
    marketingPanes.data === 'everyone' ||
    (marketingPanes.data === 'signed-in' && auth.isAuthenticated);

  return (
    <div className="container mx-auto max-w-5xl py-16">
      {/* The way in, where a visitor looks first: the top right, above the
          fold on a laptop. One link to the corporate sign-up page, which
          offers the trial and the subscription on every plan; a menu here
          asked the same question twice (product owner, 2026-10-06). Only an
          instance that pitches a site shows it. */}
      {showPanes && (
        <div className="-mt-10 mb-4 flex justify-end">
          <a href="https://taurusystems.com/sign-up/?plan=trial" className={buttonVariants({ variant: 'primary', size: 'md' })}>
            Free Trial
          </a>
        </div>
      )}
      <div className="text-center">
        {customLogo ? (
          <img
            src={customLogo}
            alt={title}
            className="mx-auto max-h-32 w-auto max-w-sm"
            draggable={false}
          />
        ) : (
          <img
            src="/tres-logo.png"
            alt="TReS"
            className="mx-auto h-20 w-20 rounded-lg shadow-sm"
            draggable={false}
          />
        )}
        <h1 className="mt-6 text-5xl font-semibold tracking-tight text-tres-text-primary">
          {title}
        </h1>
        {showTagline && <p className="mt-3 text-xl text-tres-text-primary">{tagline}</p>}
      </div>

      {showPanes && <CapabilityPanes />}

      {auth.isAuthenticated && (
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-tres-text-secondary">
          <span>
            Signed in as <span className="font-medium">{greeting}</span>
          </span>
          {me.data && (
            <>
              <span aria-hidden>·</span>
              <span>
                Role <span className="font-mono">{me.data.role}</span>
              </span>
            </>
          )}
          <VdbBadge />
        </div>
      )}

      <footer className="mt-16 flex flex-col items-center gap-1 border-t border-border pt-6 text-xs text-tres-text-secondary">
        <div>
          TReS Cloud
          {version.data && <> · v{version.data}</>}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="mailto:info@taurusystems.com"
            className="underline hover:text-tres-text-primary"
          >
            info@taurusystems.com
          </a>
          <span aria-hidden>·</span>
          <Link to="/docs/terms-of-service" className="underline hover:text-tres-text-primary">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}

function friendlyName(email: unknown, sub: unknown): string {
  if (typeof email === 'string' && email.length > 0) return email;
  if (typeof sub === 'string' && sub.length > 0) return sub;
  return 'signed in';
}

